import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveRefresh } from '../../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import { Plus, ClipboardList, Check, X, Ban, Lock, Trash2 } from 'lucide-react';
import { Button, Modal, StatusPill, Select, TimeInput24 } from '../../../components';
import { homeAgencyService } from '../../../services/agencyService';
import {
  CARD, inputClass, FieldLabel, FilterTabs, Segmented, EmptyCard, ReasonModal, ShiftLine, BookingPill,
  errorMessage, money, plural, dayLabel, timeRange, WEEKDAYS, localToday, addDays, REQUEST_STATUS,
} from '../../agency/shared';

/**
 * Agencies > Cover requests. The home asks its connected agencies for staff
 * on one date or a run of dates; each date is its own request with its own
 * places. Agencies answer with NAMED staff and an hourly charge, and the home
 * confirms (or turns down) each person — every confirmation is a booking that
 * appears on the rota's Agency group.
 */

const MAX_SHIFTS = 60;
const isoWeekday = (ymd) => new Date(`${ymd}T12:00:00`).getDay() || 7;
// Has this shift already started? Agencies can't offer on a started shift
// (offers close at the start time), so it can't be asked for — the API
// refuses it too.
const hasStarted = (date, start) => !!date && !!start && new Date(`${date}T${start}:00`) <= new Date();

const NewRequest = ({ open, agencies, roles, onClose, onSent }) => {
  const [mode, setMode] = useState('single');
  const [form, setForm] = useState({});
  const [dates, setDates] = useState([]); // multi-date preview: [{ date, start, end, places }]
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const today = localToday();
    // Past 08:00 already? Then the default shift is tomorrow's.
    const first = hasStarted(today, '08:00') ? addDays(today, 1) : today;
    setMode('single');
    setForm({
      job_role: 'Carer', note: '', date: first, start: '08:00', end: '20:00', places: 1,
      from: first, to: addDays(first, 6), weekdays: [1, 2, 3, 4, 5, 6, 7],
      agency_ids: agencies.map((a) => a.id),
    });
    setDates([]);
    setError('');
  }, [open, agencies]);

  const setValue = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const set = (k) => (e) => setValue(k)(e.target.value);
  const toggleAgency = (id) => setForm((f) => ({
    ...f, agency_ids: f.agency_ids.includes(id) ? f.agency_ids.filter((x) => x !== id) : [...f.agency_ids, id],
  }));

  const buildPreview = () => {
    const out = [];
    let skipped = 0;
    for (let d = form.from; d <= form.to && out.length < MAX_SHIFTS + 1; d = addDays(d, 1)) {
      if (!form.weekdays.includes(isoWeekday(d))) continue;
      // Today's shift has already started — it can't be covered.
      if (hasStarted(d, form.start)) { skipped += 1; continue; }
      out.push({ date: d, start: form.start, end: form.end, places: Number(form.places) || 1 });
    }
    if (out.length > MAX_SHIFTS) { setError(`Choose no more than ${MAX_SHIFTS} shifts at once.`); return; }
    if (!out.length) {
      setError(skipped ? `${form.start} has already passed on the dates chosen — pick a later start or from tomorrow.` : 'No dates match — check the range and weekdays.');
      return;
    }
    setError(skipped ? `Left out ${skipped === 1 ? 'today' : `${skipped} dates`} — ${form.start} has already passed.` : '');
    setDates(out);
  };

  const send = async () => {
    const shifts = mode === 'single'
      ? [{ date: form.date, start: form.start, end: form.end, places: Number(form.places) || 1 }]
      : dates;
    if (!shifts.length) { setError('Preview the dates first.'); return; }
    const started = shifts.filter((s) => hasStarted(s.date, s.start));
    if (started.length) {
      setError(mode === 'single'
        ? `That shift has already started (${form.start}). Choose a start time later than now.`
        : `${started.map((s) => `${dayLabel(s.date)} ${s.start}`).join(', ')} ${started.length === 1 ? 'has' : 'have'} already started — change the time or leave ${started.length === 1 ? 'it' : 'them'} out.`);
      return;
    }
    if (!form.agency_ids.length) { setError('Choose at least one agency.'); return; }
    setBusy(true); setError('');
    try {
      const res = await homeAgencyService.createRequests({
        job_role: form.job_role, note: form.note, agency_ids: form.agency_ids, shifts,
      });
      toast.success(res.message);
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // The form is filled by the effect above, one render after opening.
  if (!open || !form.agency_ids) return null;
  const singleStarted = mode === 'single' && hasStarted(form.date, form.start);
  return (
    <Modal
      isOpen
      onClose={busy ? undefined : onClose}
      title="New cover request"
      maxWidth="max-w-2xl"
      footer={(
        <div className="flex justify-between items-center gap-2">
          <span className="text-[12.5px] text-[#64748B]">
            {mode === 'multi' && dates.length ? plural(dates.length, 'shift') : ''}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={send} loading={busy} disabled={(mode === 'multi' && !dates.length) || singleStarted}>Send to agencies</Button>
          </div>
        </div>
      )}
    >
      <div className="flex flex-col gap-4">
        <Segmented value={mode} onChange={(v) => { setMode(v); setDates([]); setError(''); }} options={[{ value: 'single', label: 'One shift' }, { value: 'multi', label: 'Several dates' }]} />

        {/* Dates get a column each wide enough for dd/mm/yyyy + the calendar
            icon; times are 24-hour (TimeInput24) so 23:00–00:00 can't be
            misread as 11:00–12:00. */}
        {mode === 'single' ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel required>Role needed</FieldLabel>
                <Select value={form.job_role} onChange={(v) => setForm((f) => ({ ...f, job_role: v }))} options={roles} />
              </div>
              <div><FieldLabel required>Date</FieldLabel><input type="date" className={inputClass} value={form.date} min={localToday()} onChange={set('date')} /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <FieldLabel required>Start</FieldLabel>
                <TimeInput24 label="Start" value={form.start} onChange={setValue('start')} className={singleStarted ? '!border-rose-400' : ''} />
                {singleStarted && <p className="mt-1 text-[11.5px] text-rose-600">Already started — pick a later time.</p>}
              </div>
              <div><FieldLabel required hint={form.end <= form.start ? 'Next day' : undefined}>End</FieldLabel><TimeInput24 label="End" value={form.end} onChange={setValue('end')} /></div>
              <div><FieldLabel required>Staff</FieldLabel><input type="number" min="1" max="10" className={inputClass} value={form.places} onChange={set('places')} /></div>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <FieldLabel required>Role needed</FieldLabel>
                <Select value={form.job_role} onChange={(v) => setForm((f) => ({ ...f, job_role: v }))} options={roles} />
              </div>
              <div><FieldLabel required>From</FieldLabel><input type="date" className={inputClass} value={form.from} min={localToday()} onChange={(e) => { set('from')(e); setDates([]); }} /></div>
              <div><FieldLabel required>To</FieldLabel><input type="date" className={inputClass} value={form.to} min={form.from} onChange={(e) => { set('to')(e); setDates([]); }} /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><FieldLabel required>Start</FieldLabel><TimeInput24 label="Start" value={form.start} onChange={(v) => { setValue('start')(v); setDates([]); }} /></div>
              <div><FieldLabel required hint={form.end <= form.start ? 'Next day' : undefined}>End</FieldLabel><TimeInput24 label="End" value={form.end} onChange={(v) => { setValue('end')(v); setDates([]); }} /></div>
              <div><FieldLabel required>Staff each</FieldLabel><input type="number" min="1" max="10" className={inputClass} value={form.places} onChange={(e) => { set('places')(e); setDates([]); }} /></div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((d) => {
                const on = form.weekdays.includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => { setForm((f) => ({ ...f, weekdays: on ? f.weekdays.filter((x) => x !== d.value) : [...f.weekdays, d.value] })); setDates([]); }}
                    className={`h-8 w-11 rounded-lg text-[12px] font-semibold border ${on ? 'bg-blue-600 border-blue-600 text-white' : 'border-[#DCE4EF] dark:border-gray-700 text-[#475569] dark:text-gray-300'}`}
                  >{d.short}</button>
                );
              })}
              <Button size="sm" variant="secondary" className="ml-auto" onClick={buildPreview}>Preview dates</Button>
            </div>
            {dates.length > 0 && (
              <div className="max-h-[220px] overflow-y-auto rounded-xl border border-[#E3E9F3] dark:border-gray-800 divide-y divide-[#EEF2F6] dark:divide-gray-800">
                {dates.map((d, i) => (
                  <div key={d.date} className="grid grid-cols-[1fr_112px_112px_64px_auto] gap-2 items-center px-3 py-2">
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold">{dayLabel(d.date)}</span>
                      {d.end <= d.start && <span className="block text-[11px] text-[#94A3B8]">Ends next day</span>}
                      {hasStarted(d.date, d.start) && <span className="block text-[11px] font-semibold text-rose-600">Already started</span>}
                    </span>
                    <TimeInput24 size="sm" label={`${dayLabel(d.date)} start`} value={d.start} onChange={(v) => setDates((xs) => xs.map((x, j) => (j === i ? { ...x, start: v } : x)))} />
                    <TimeInput24 size="sm" label={`${dayLabel(d.date)} end`} value={d.end} onChange={(v) => setDates((xs) => xs.map((x, j) => (j === i ? { ...x, end: v } : x)))} />
                    <input type="number" min="1" max="10" className={`${inputClass} h-8`} value={d.places} onChange={(e) => setDates((xs) => xs.map((x, j) => (j === i ? { ...x, places: Number(e.target.value) || 1 } : x)))} />
                    <Button size="sm" variant="ghost" icon={<Trash2 className="w-4 h-4" />} aria-label="Leave out this date" onClick={() => setDates((xs) => xs.filter((_, j) => j !== i))} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <FieldLabel required>Send to</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {agencies.map((a) => (
              <label key={a.id} className={`flex items-center gap-2 h-9 px-3 rounded-full border text-[13px] cursor-pointer ${form.agency_ids.includes(a.id) ? 'border-blue-400 bg-[#F3F7FF] dark:bg-blue-500/10' : 'border-[#DCE4EF] dark:border-gray-700'}`}>
                <input type="checkbox" checked={form.agency_ids.includes(a.id)} onChange={() => toggleAgency(a.id)} />
                {a.name}
              </label>
            ))}
          </div>
        </div>
        <div>
          <FieldLabel hint="Agencies see this">Note</FieldLabel>
          <input className={inputClass} value={form.note} onChange={set('note')} maxLength={500} placeholder="e.g. Dementia experience preferred" />
        </div>
        {error && <p className="text-[12.5px] text-rose-600">{error}</p>}
      </div>
    </Modal>
  );
};

const RequestCard = ({ r, onConfirm, onReject, onCancelBooking, onCancel, onClose, busyId }) => {
  const meta = REQUEST_STATUS[r.status] || { label: r.status, tone: 'mute' };
  const offers = r.bookings.filter((b) => b.status === 'offered');
  const booked = r.bookings.filter((b) => ['confirmed', 'no_show'].includes(b.status));
  const live = r.status !== 'cancelled' && !r.ended;
  return (
    <div className={`${CARD} p-5 flex flex-col gap-3`}>
      <div className="flex items-start justify-between gap-3">
        <ShiftLine item={r} />
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
          <span className="text-[11.5px] text-[#94A3B8]">{r.reference}{r.group_ref ? ` · ${r.group_ref}` : ''}</span>
        </div>
      </div>
      <p className="text-[13px] text-[#334155] dark:text-gray-300">
        <strong>{r.confirmed}</strong> of {r.places - r.closed_places} booked
        {r.closed_places > 0 && <span className="text-[#94A3B8]"> · {r.closed_places} closed</span>}
        {r.open_places > 0 && <span className="text-amber-700"> · {plural(r.open_places, 'place')} open</span>}
      </p>
      <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
        Sent to {r.agencies.map((a) => `${a.name}${a.declined ? ' (can\'t cover)' : ''}`).join(', ')}
      </p>
      {r.cancel_reason && <p className="text-[12.5px] text-[#64748B]">Cancelled: {r.cancel_reason}</p>}

      {offers.length > 0 && (
        <div className="rounded-xl bg-[#F3F7FF] dark:bg-blue-500/10 p-3 flex flex-col gap-2">
          <p className="text-[12px] font-bold text-blue-700 dark:text-blue-300">Offers to review</p>
          {offers.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-[#0B1220] dark:text-gray-100">{b.worker?.name} <span className="font-normal text-[#64748B]">· {b.agency?.name} · {money(b.hourly_rate)}/h</span></p>
                {b.note && <p className="text-[12px] text-[#64748B]">{b.note}</p>}
              </div>
              {live && (
                <div className="flex gap-1.5">
                  <Button size="sm" leftIcon={<Check className="w-4 h-4" />} loading={busyId === b.id} disabled={!r.open_places} onClick={() => onConfirm(b)}>Confirm</Button>
                  <Button size="sm" variant="secondary" leftIcon={<X className="w-4 h-4" />} onClick={() => onReject(b)}>Turn down</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {booked.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {booked.map((b) => (
            <li key={b.id} className="flex items-center justify-between gap-2 text-[13px]">
              <span className="truncate">{b.worker?.name} <span className="text-[#94A3B8]">· {b.agency?.name} · {money(b.hourly_rate)}/h</span></span>
              <span className="flex items-center gap-2">
                <BookingPill booking={b} />
                {b.state === 'upcoming' && (
                  <button type="button" className="text-[12px] font-semibold text-rose-600 hover:underline" onClick={() => onCancelBooking(b)}>Cancel</button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {live && (
        <div className="flex flex-wrap gap-2 pt-1">
          {r.open_places > 0 && booked.length > 0 && (
            <Button size="sm" variant="secondary" leftIcon={<Lock className="w-4 h-4" />} onClick={() => onClose(r)}>Close remaining places</Button>
          )}
          {!booked.length && (
            <Button size="sm" variant="danger-soft" leftIcon={<Ban className="w-4 h-4" />} onClick={() => onCancel(r)}>Cancel request</Button>
          )}
        </div>
      )}
    </div>
  );
};

const RequestsTab = ({ agencies, onChanged }) => {
  const [view, setView] = useState('open');
  const [filter, setFilter] = useState('needs_cover');
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(null); // { kind, item }

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await homeAgencyService.requests({ view });
      setRows(res.data || []);
      setRoles((res.meta?.job_roles || []).map((r) => ({ value: r, label: r })));
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load cover requests.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [view]);
  useEffect(() => { load(); }, [load]);
  // Offers, "can't cover" and agency cancellations arrive without a reload; a
  // New cover request form being filled in is left alone.
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const refresh = () => { load(); onChanged(); };

  const preds = {
    needs_cover: (r) => ['needs_cover', 'offers'].includes(r.status),
    offers: (r) => r.pending_offers > 0,
    covered: (r) => r.status === 'covered',
    all: () => true,
  };
  const list = useMemo(() => (view === 'past' ? rows : rows.filter(preds[filter])), [rows, view, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (b) => {
    setBusyId(b.id);
    try {
      const res = await homeAgencyService.confirm(b.id);
      toast.success(res.message);
      refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const DIALOGS = {
    reject: {
      title: (i) => `Turn down ${i.worker?.name}`, label: 'Reason (optional)', required: false, confirm: 'Turn down',
      intro: 'The agency can offer someone else.', run: (i, t) => homeAgencyService.reject(i.id, t),
    },
    cancelBooking: {
      title: (i) => `Cancel ${i.worker?.name}'s booking`, confirm: 'Cancel booking',
      intro: 'The place opens again. The agency and the worker are told.', run: (i, t) => homeAgencyService.cancelBooking(i.id, t),
    },
    cancel: {
      title: (i) => `Cancel ${i.reference}`, confirm: 'Cancel request',
      intro: 'Outstanding offers are withdrawn and the agencies are told.', run: (i, t) => homeAgencyService.cancelRequest(i.id, t),
    },
    close: {
      title: (i) => `Close remaining places on ${i.reference}`, confirm: 'Close places', variant: 'primary',
      intro: 'Staff already booked stay booked; you stop looking for the rest.', run: (i, t) => homeAgencyService.closeRequest(i.id, t),
    },
  };
  const d = dialog && DIALOGS[dialog.kind];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {view === 'open' ? (
          <FilterTabs
            value={filter}
            onChange={setFilter}
            tabs={[
              { value: 'needs_cover', label: 'Needs cover', count: rows.filter(preds.needs_cover).length },
              { value: 'offers', label: 'Offers to review', count: rows.filter(preds.offers).length, alert: true },
              { value: 'covered', label: 'Covered', count: rows.filter(preds.covered).length },
              { value: 'all', label: 'All', count: rows.length },
            ]}
          />
        ) : <span />}
        <div className="flex items-center gap-2">
          <Segmented size="sm" value={view} onChange={setView} options={[{ value: 'open', label: 'Upcoming' }, { value: 'past', label: 'Past' }]} />
          <Button leftIcon={<Plus className="w-4 h-4" />} disabled={!agencies.length} onClick={() => setCreating(true)}>New cover request</Button>
        </div>
      </div>
      {!agencies.length && (
        <p className="text-[13px] text-[#64748B]">Connect to an agency first — find one under Connections.</p>
      )}

      {!loading && !list.length && (
        <EmptyCard icon={ClipboardList} title="No cover requests here" text="Ask your connected agencies for staff on one date or a run of dates." />
      )}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {list.map((r) => (
          <RequestCard
            key={r.id}
            r={r}
            busyId={busyId}
            onConfirm={confirm}
            onReject={(b) => setDialog({ kind: 'reject', item: b })}
            onCancelBooking={(b) => setDialog({ kind: 'cancelBooking', item: b })}
            onCancel={(x) => setDialog({ kind: 'cancel', item: x })}
            onClose={(x) => setDialog({ kind: 'close', item: x })}
          />
        ))}
      </div>

      <NewRequest
        open={creating}
        agencies={agencies}
        roles={roles.length ? roles : [{ value: 'Carer', label: 'Carer' }]}
        onClose={() => setCreating(false)}
        onSent={() => { setCreating(false); setView('open'); setFilter('needs_cover'); refresh(); }}
      />
      <ReasonModal
        open={Boolean(d)}
        title={d ? d.title(dialog.item) : ''}
        intro={d?.intro}
        label={d?.label || 'Reason'}
        required={d?.required !== false}
        confirmLabel={d?.confirm}
        variant={d?.variant || 'danger'}
        onClose={() => setDialog(null)}
        onSubmit={async (text) => {
          const res = await d.run(dialog.item, text);
          toast.success(res.message);
          setDialog(null);
          refresh();
        }}
      />
    </div>
  );
};

export default RequestsTab;
